import FormDialog from "./FormDialog";
import {IconButton, Table, TableBody, TableCell, TableRow} from "@mui/material";
import {DeleteIcon, EditIcon} from "../icons";
import {UpdateFormHandler} from "./Form";
import FormattedTableHead from "../FormattedTableHead";
import DeleteDialog from "./DeleteDialog";
import {useAddressBookCRUD} from "@/components/address-book/tools";
import {MutationFunction} from "@/utils/Form";
import {UpdateAddressBookEntryMutation, UpdateAddressBookEntryMutationVariables} from "./queries.generated";
import {styled} from "@mui/system";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {useState} from "react";
import InfoPopup from "../InfoPopup";
import {defineMessage} from "react-intl";

const ActionCell = styled(TableCell)(() => ({
    whiteSpace: "nowrap",
    textAlign: "right",
}));

/**
 * To not break the layout of the table, the content of the action cell must be wrapped in a flex container.
 * Otherwise the divider might appear disalligned when one of the content of the other cells is taller or shorter than the others.
 */
const ActionContentContainer = styled("div")(() => ({
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
}));

type Props = {
    target: string;
    updateMutation: MutationFunction<UpdateAddressBookEntryMutation, UpdateAddressBookEntryMutationVariables>;
} & ReturnType<typeof useAddressBookCRUD>;

export const AddressBookCRUD = ({
    data,
    deleteEntryName,
    formHandler,
    refetch,
    setDeleteEntryName,
    setFormHandler,
    updateMutation,
    target,
}: Props) => {
    const [internalEntryInfoShown, setInternalEntryInfoShown] = useState(false);

    return (
        <>
            <FormDialog
                target={target}
                close={() => setFormHandler(null)}
                onSuccess={() => refetch()}
                handler={formHandler}
            />
            <DeleteDialog
                close={() => setDeleteEntryName(null)}
                cn={deleteEntryName}
                onSuccess={() => {
                    setDeleteEntryName(null);
                    refetch();
                }}
                target={target}
            />
            <InfoPopup
                open={internalEntryInfoShown}
                onClose={() => setInternalEntryInfoShown(false)}
                translationId={defineMessage({
                    id: "address_book.internal_entry_info",
                    defaultMessage: "Internal entries can not be edited or deleted",
                })}
            />
            <Table>
                <FormattedTableHead
                    cells={[
                        {label: defineMessage({id: "address_book.sn", defaultMessage: "Surname"})},
                        {label: defineMessage({id: "address_book.givenName", defaultMessage: "Given name"})},
                        {
                            label: defineMessage({
                                id: "address_book.telephoneNumber",
                                defaultMessage: "Telephone number",
                            }),
                        },
                        {label: defineMessage({id: "address_book.mail", defaultMessage: "Email"})},
                        {
                            label: defineMessage({
                                id: "address_book.departmentNumber",
                                defaultMessage: "Department number",
                            }),
                        },
                        {label: defineMessage({id: "address_book.o", defaultMessage: "Company"})},
                        {label: defineMessage({id: "address_book.mobile", defaultMessage: "Mobile number"})},
                        {label: defineMessage({id: "address_book.homePhone", defaultMessage: "Home number"})},
                        {label: defineMessage({id: "address_book.description", defaultMessage: "Description"})},
                        {},
                    ]}
                />
                <TableBody>
                    {data?.addressBook.entries.map((entry) => (
                        <TableRow key={entry.cn}>
                            <TableCell>{entry.sn}</TableCell>
                            <TableCell>{entry.givenName}</TableCell>
                            <TableCell>{entry.telephoneNumber?.join(", ")}</TableCell>
                            <TableCell>{entry.mail}</TableCell>
                            <TableCell>{entry.departmentNumber}</TableCell>
                            <TableCell>{entry.o}</TableCell>
                            <TableCell>{entry.mobile}</TableCell>
                            <TableCell>{entry.homePhone}</TableCell>
                            <TableCell>{entry.description}</TableCell>
                            <ActionCell style={{whiteSpace: "nowrap"}}>
                                <EditDeleteIfExternal
                                    internal={entry.uid != null}
                                    setFormHandler={() => {
                                        setFormHandler(new UpdateFormHandler(entry.cn, entry, updateMutation));
                                    }}
                                    setDeleteEntryName={() => setDeleteEntryName(entry.cn)}
                                    showInternalEntryInfo={() => setInternalEntryInfoShown(true)}
                                />
                            </ActionCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </>
    );
};

type EditDeleteIfExternalProps = {
    internal: boolean;
    showInternalEntryInfo: () => void;
    setFormHandler?: () => void;
    setDeleteEntryName?: () => void;
};

const EditDeleteIfExternal = ({
    internal,
    showInternalEntryInfo: setInternalEntryInfoShown,
    setFormHandler,
    setDeleteEntryName,
}: EditDeleteIfExternalProps) => {
    if (internal)
        return (
            <ActionContentContainer>
                <IconButton color="primary" size="small" onClick={setInternalEntryInfoShown}>
                    <FontAwesomeIcon icon="info" fixedWidth />
                </IconButton>
            </ActionContentContainer>
        );

    return (
        <ActionContentContainer>
            <IconButton color="primary" size="small" onClick={setFormHandler}>
                <EditIcon />
            </IconButton>
            <IconButton color="secondary" size="small" onClick={setDeleteEntryName}>
                <DeleteIcon />
            </IconButton>
        </ActionContentContainer>
    );
};
